import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import {
  useCreateFieldTemplate,
  useUpdateFieldTemplate,
  type TaxonomyFieldTemplate,
} from '@/entities/taxonomy-field-template';
import { resolveCategoryIcon, taxonomyCategoriesQueryOptions } from '@/entities/taxonomy-category';
import {
  DEFAULT_DATE_MASK,
  isCompilableFieldPattern,
  TAXONOMY_DATE_MASK_OPTIONS,
  TAXONOMY_FIELD_TYPE_LABELS,
  TAXONOMY_LEVEL_OPTIONS,
  TAXONOMY_SELECTABLE_FIELD_TYPES,
  TAXONOMY_TEXT_TRANSFORM_OPTIONS,
  taxonomyDateMaskExample,
  type TaxonomyFieldGroup,
  type TaxonomyFieldType,
  type TaxonomyTextTransform,
} from '@/entities/taxonomy';
import { FormField, SectionHeading } from '@/shared/ui/FormDialog';
import { FormSheet } from '@/shared/ui/FormSheet';
import { ListBindingFields } from '../../../TaxonomyDetail/components/ListBinding/ListBindingFields';
import { useListBinding } from '../../../TaxonomyDetail/components/ListBinding/useListBinding';
import { standardKeyOf } from '../../lib/standardKey';

export interface FieldTemplateModalProps {
  open: boolean;
  /** Existing template to edit; null creates a new one. */
  template: TaxonomyFieldTemplate | null;
  onClose: () => void;
}

const UNSET_CATEGORY = '__unset_category__';

/* SectionHeading and FormField live in shared/ui/FormDialog alongside the shell
   that first needed them, so this sheet and the schema builder's "Add field"
   stay visually identical whichever chrome wraps them. */

/** A boolean default, framed as a card so it reads as a control, not a caption. */
function SwitchCard({
  title,
  description,
  checked,
  onCheckedChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-border-light p-3 hover:bg-accent/40">
      <span className="flex flex-col">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={title} />
    </label>
  );
}

/**
 * Create/edit one reusable field definition. Carries the same governance
 * attributes as a schema field, so dropping it into a schema yields a
 * fully-configured column rather than a bare name.
 *
 * Laid out as a two-column grid grouped into Definition / Placement /
 * Governance. Twelve controls in one narrow column forced an inner scrollbar
 * and pushed governance below the fold, so the attributes that decide how a
 * field behaves were the ones nobody saw.
 */
export function FieldTemplateModal({ open, template, onClose }: FieldTemplateModalProps) {
  const { t } = useTranslation('knowledge');
  const nameId = useId();
  const descriptionId = useId();
  const formatId = useId();
  const maskListId = useId();
  const patternId = useId();

  // The category registry — the ONLY source of categories now.
  const categoriesQuery = useQuery(taxonomyCategoriesQueryOptions());

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [group, setGroup] = useState<TaxonomyFieldGroup>('media');
  const [type, setType] = useState<TaxonomyFieldType>('dropdown');
  // eslint-disable-next-line i18next/no-literal-string -- the stored `level` value
  const [level, setLevel] = useState('all');
  const [required, setRequired] = useState(false);
  const [unique, setUnique] = useState(false);
  const [format, setFormat] = useState('');
  const [validationPattern, setValidationPattern] = useState('');
  const [transform, setTransform] = useState<TaxonomyTextTransform>('none');
  const binding = useListBinding({ open, type, initialListRef: template?.listRef });

  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? '');
    setDescription(template?.description ?? '');
    setCategory(template?.category ?? '');
    setGroup(template?.group ?? 'media');
    setType(template?.type ?? 'dropdown');
    // eslint-disable-next-line i18next/no-literal-string -- the stored `level` value
    setLevel(template?.level ?? 'all');
    setRequired(template?.required ?? false);
    setUnique(template?.unique ?? false);
    setFormat(template?.format ?? '');
    setValidationPattern(template?.validationPattern ?? '');
    setTransform(template?.transform ?? 'none');
  }, [open, template]);

  const create = useCreateFieldTemplate();
  const update = useUpdateFieldTemplate(template?.id ?? '');

  // Gate on the API's own storability rule rather than `new RegExp` — see the
  // note on the same check in `EditFieldModal`.
  const trimmedPattern = validationPattern.trim();
  const patternError =
    trimmedPattern && !isCompilableFieldPattern(trimmedPattern)
      ? t('taxonomy.fieldTemplateModal.notValidRegularExpression')
      : null;

  const trimmed = name.trim();
  const pending = create.isPending || update.isPending;
  const maskExample = taxonomyDateMaskExample(format);

  const canSave =
    trimmed.length > 0 && !!category.trim() && !patternError && !pending && !binding.blocksSave;

  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);

  /**
   * The field points at a category that no longer exists (renamed or deleted
   * elsewhere). Kept as a selectable option so opening the modal cannot
   * silently blank it — a save would otherwise re-file the field.
   */
  const orphanCategory = useMemo(() => {
    if (!category) return null;
    // Case-insensitively, matching the server: a template holding `campaign`
    // does belong to the `Campaign` row, so calling it orphaned would offer to
    // re-file a field that is already filed.
    const key = category.trim().toLowerCase();
    return categories.some((c) => c.name.trim().toLowerCase() === key) ? null : category;
  }, [category, categories]);

  /**
   * The `listRef` slice of the payload. The key's presence is itself an instruction to the
   * service: `null` unbinds, and omitting it leaves the stored binding alone.
   */
  const listRefPatch = () => {
    const change = binding.change();
    if (change.kind === 'unbind') return { listRef: null };
    if (change.kind === 'bind') return { listRef: change.listRef };
    return {};
  };

  const handleSave = () => {
    if (!canSave) return;
    const payload = {
      name: trimmed,
      description: description.trim() || undefined,
      category: category.trim(),
      group,
      type,
      level,
      required,
      unique,
      // Belt and braces alongside the type-change reset: `format` is only
      // meaningful on a date, so never persist one on another type.
      format: (type === 'date' && format.trim()) || undefined,
      validationPattern: validationPattern.trim() || undefined,
      transform,
      ...listRefPatch(),
    };
    if (template) {
      // Backfill the catalog key on rows seeded before it existed, so provenance
      // (and the "Edited standard" badge) survives a rename.
      const libraryKey = standardKeyOf(template);
      update.mutate({ ...payload, ...(libraryKey ? { libraryKey } : {}) }, { onSuccess: onClose });
    } else {
      // TEMPORARY, for the moderated tester run: this modal has no scope control,
      // so a new template would otherwise be created org-wide and every tester
      // would see it. Sent on CREATE only — on update it would re-scope a template
      // on each edit. Drop `workspaceScoped` when the run is over.
      create.mutate({ ...payload, workspaceScoped: true }, { onSuccess: onClose });
    }
  };

  return (
    <FormSheet
      className="w-[40rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={
        template
          ? t('taxonomy.fieldTemplateModal.editField')
          : t('taxonomy.fieldTemplateModal.newReusableField')
      }
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <FormField label={t('taxonomy.fieldTemplateModal.fieldName')} htmlFor={nameId} required>
            <Input
              id={nameId}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('taxonomy.fieldTemplateModal.eGObjective')}
              maxLength={100}
              className="bg-input"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
              autoFocus
            />
          </FormField>

          <FormField
            label={t('taxonomy.fieldTemplateModal.descriptionOptional')}
            htmlFor={descriptionId}
          >
            <Input
              id={descriptionId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('taxonomy.fieldTemplateModal.whatField')}
              maxLength={500}
              className="bg-input"
            />
          </FormField>
        </div>

        <div className="flex flex-col gap-4 border-t border-border-light pt-5">
          <SectionHeading>{t('taxonomy.fieldTemplateModal.placement')}</SectionHeading>

          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            {/* Choose from the registry — categories are created and iconed on
                the Categories tab, never invented here. Free text produced
                near-duplicate groups ("Campaign"/"campaigns") that nobody
                owned and that carried no icon or ordering. */}
            <FormField
              label={t('taxonomy.fieldTemplateModal.category')}
              required
              hint={
                orphanCategory
                  ? t('taxonomy.fieldTemplateModal.orphanCategory', { name: orphanCategory })
                  : t('taxonomy.fieldTemplateModal.managedCategoriesTab')
              }
              error={
                categories.length === 0
                  ? t('taxonomy.fieldTemplateModal.createCategoryFirstCategoriesTab')
                  : null
              }
            >
              <Select
                value={category || UNSET_CATEGORY}
                onValueChange={(v) => setCategory(v === UNSET_CATEGORY ? '' : v)}
                disabled={categories.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('taxonomy.fieldTemplateModal.chooseCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {/* A field whose category was renamed or deleted still has to
                      render its current value, or the Select would silently
                      blank it and a save would wipe the grouping. */}
                  {orphanCategory && (
                    <SelectItem value={orphanCategory}>
                      {t('taxonomy.fieldTemplateModal.missingCategory', { name: orphanCategory })}
                    </SelectItem>
                  )}
                  {categories.map((c) => {
                    const Icon = resolveCategoryIcon(c.icon);
                    return (
                      <SelectItem key={c.id} value={c.name}>
                        <span className="flex items-center gap-2">
                          <Icon className="size-4 text-muted-foreground" aria-hidden />
                          {c.name}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FormField>

            <FormField
              label={t('taxonomy.fieldTemplateModal.section')}
              // eslint-disable-next-line i18next-templates/no-literal-string -- element id, not copy
              labelId={`${nameId}-section`}
              hint={t('taxonomy.fieldTemplateModal.whichHalfSchemaLands')}
            >
              <ToggleGroup
                aria-labelledby={`${nameId}-section`}
                type="single"
                value={group}
                onValueChange={(v) => v && setGroup(v as TaxonomyFieldGroup)}
                variant="outline"
                size="sm"
                className="grid w-full grid-cols-2"
              >
                {/* eslint-disable i18next/no-literal-string -- TaxonomyFieldGroup wire enum */}
                <ToggleGroupItem value="media">
                  {t('taxonomy.fieldTemplateModal.mediaField')}
                </ToggleGroupItem>
                <ToggleGroupItem value="content">
                  {t('taxonomy.fieldTemplateModal.contentField')}
                </ToggleGroupItem>
                {/* eslint-enable i18next/no-literal-string */}
              </ToggleGroup>
            </FormField>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-border-light pt-5">
          <SectionHeading>{t('taxonomy.fieldTemplateModal.dataType')}</SectionHeading>

          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
            <FormField label={t('taxonomy.fieldTemplateModal.hierarchyLevel')}>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAXONOMY_LEVEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField label={t('taxonomy.fieldTemplateModal.type')}>
              <Select
                value={type}
                onValueChange={(v) => {
                  const next = v as TaxonomyFieldType;
                  setType(next);
                  // Drop the mask when leaving Date, so switching away and back
                  // can't resurrect a format the user can no longer see.
                  if (next !== 'date') setFormat('');
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAXONOMY_SELECTABLE_FIELD_TYPES.map((fieldType) => (
                    <SelectItem key={fieldType} value={fieldType}>
                      {TAXONOMY_FIELD_TYPE_LABELS[fieldType]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            {type === 'dropdown' && <ListBindingFields binding={binding} showFollow={false} />}

            {/* Only dates have a format: the mask shapes what the date picker
                WRITES. On any other type the input did nothing at all, which
                is what made this attribute read as pointless. */}
            {type === 'date' && (
              <FormField
                label={t('taxonomy.fieldTemplateModal.dateFormat')}
                htmlFor={formatId}
                hint={
                  maskExample
                    ? t('taxonomy.fieldTemplateModal.writesDatesAs', { example: maskExample })
                    : t('taxonomy.fieldTemplateModal.leaveBlankForMask', {
                        mask: DEFAULT_DATE_MASK,
                      })
                }
              >
                <Input
                  id={formatId}
                  list={maskListId}
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  placeholder={DEFAULT_DATE_MASK}
                  maxLength={40}
                  className="bg-input font-mono"
                />
                {/* A datalist, not a Select: the common masks are one click
                    away but an unusual one (MMDD-MMDD for a flight window) is
                    still typeable. `format` is a free string by design. */}
                <datalist id={maskListId}>
                  {TAXONOMY_DATE_MASK_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.example}
                    </option>
                  ))}
                </datalist>
              </FormField>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-border-light pt-5">
          <SectionHeading>{t('taxonomy.fieldTemplateModal.governance')}</SectionHeading>

          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            <FormField
              label={t('taxonomy.fieldTemplateModal.validationPatternOptional')}
              htmlFor={patternId}
              error={patternError}
            >
              <Input
                id={patternId}
                value={validationPattern}
                onChange={(e) => setValidationPattern(e.target.value)}
                placeholder={t('taxonomy.fieldTemplateModal.regexEGZ2')}
                maxLength={500}
                className="bg-input font-mono"
              />
            </FormField>

            <FormField label={t('taxonomy.fieldTemplateModal.textTransformation')}>
              <Select
                value={transform}
                onValueChange={(v) => setTransform(v as TaxonomyTextTransform)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAXONOMY_TEXT_TRANSFORM_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <SwitchCard
              title={t('taxonomy.fieldTemplateModal.required')}
              description={t('taxonomy.fieldTemplateModal.defaultSchemasUsing')}
              checked={required}
              onCheckedChange={setRequired}
            />
            <SwitchCard
              title={t('taxonomy.fieldTemplateModal.unique')}
              description={t('taxonomy.fieldTemplateModal.eachRecordMustHaveDifferent')}
              checked={unique}
              onCheckedChange={setUnique}
            />
          </div>
        </div>

        {template && (
          <p className="text-xs text-muted-foreground">
            {t('taxonomy.fieldTemplateModal.editingDefinitionDoesNotChange')}
          </p>
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: pending
            ? t('taxonomy.fieldTemplateModal.saving')
            : template
              ? t('taxonomy.fieldTemplateModal.saveField')
              : t('taxonomy.fieldTemplateModal.addLibrary'),
          onClick: handleSave,
          disabled: !canSave,
        }}
        secondary={{ label: t('taxonomy.fieldTemplateModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

FieldTemplateModal.displayName = 'FieldTemplateModal';
